import type { EnvelopeAccountHoldingsQuery } from './graphql/envelopeAccountHoldings.generated';
import type {
  DirectSecurityPosition,
  SecurityGroupPosition,
  FinancialSecurity as SecurityHolding,
} from '@betterment/shared-components/src/components/holdings/data';

type ManagedAccount = Extract<EnvelopeAccountHoldingsQuery['account'], { __typename: 'ManagedAccount' }>;
type DirectTradingAccount = Extract<EnvelopeAccountHoldingsQuery['account'], { __typename: 'DirectTradingAccount' }>;

type GqlSecurity = ManagedAccount['securityGroupPositions'][0]['securityPositions'][0]['security'];
type GqlFinancialSecurity = ManagedAccount['securityGroupPositions'][0]['securityPositions'][0]['financialSecurity'];
type GqlFinancialSecurityDT = DirectTradingAccount['securityPositions'][0]['financialSecurity'];

export function toSecurityHolding(
  gqlSecurity: GqlSecurity,
  gqlFinancialSecurity: GqlFinancialSecurity
): SecurityHolding {
  const { id, symbol, name } = gqlFinancialSecurity;

  if (gqlFinancialSecurity.__typename === 'FinancialSecurityFund' && gqlSecurity.__typename === 'SecurityFund') {
    return { id, symbol, name, url: gqlSecurity.url, expenseRatio: gqlSecurity.expenseRatio.value };
  }

  return { id, symbol, name };
}

export function toSecurityHoldingFromDirectTrading(gqlFS: GqlFinancialSecurityDT): SecurityHolding {
  if (gqlFS.__typename === 'FinancialSecurityStock') {
    const gqlLogo = gqlFS.companyProfile?.logo;
    return {
      ...gqlFS,
      logo: gqlLogo ? { dark: gqlLogo.dark, light: gqlLogo.light } : undefined,
    };
  }
  return gqlFS;
}

export function toDirectSecurityPositions(
  gqlPositions: DirectTradingAccount['securityPositions']
): DirectSecurityPosition[] {
  return gqlPositions.map(p => ({
    ...p,
    financialSecurity: toSecurityHoldingFromDirectTrading(p.financialSecurity),
  }));
}

export function toSecurityHoldingGroupPositions(
  gqlGroupPositions: ManagedAccount['securityGroupPositions']
): SecurityGroupPosition[] {
  return gqlGroupPositions.map(groupPosition => {
    const positionBySecurityId = new Map(groupPosition.securityPositions.map(sp => [sp.financialSecurity.id, sp]));

    const securityPositions = groupPosition.securityPositionsBySecurity.map(aggregatedPosition => {
      const position = positionBySecurityId.get(aggregatedPosition.financialSecurity.id);
      if (position === undefined)
        throw new Error(
          `No matching securityPosition for financial security id: ${aggregatedPosition.financialSecurity.id}`
        );

      const financialSecurity = toSecurityHolding(position.security, position.financialSecurity);
      return {
        ...aggregatedPosition,
        financialSecurity,
        amount: position.amount,
        shares: position.shares,
      };
    });

    return {
      ...groupPosition,
      securityPositions,
    };
  });
}
